/**
 * QualityScoreStep — Wizard step 10 (Phase G4C CP2).
 *
 * Displays the 5-dimension quality score with:
 *   - Voice disclaimer at the top (locked copy from `quality_copy.js`)
 *   - Overall score dial (0-100)
 *   - Per-dimension breakdown (5 subscores + short explainer each)
 *   - Weak-section highlights (sections below
 *     `body.thresholds.weak_section_score`) with a
 *     "Recommended improvement" chip + "Regenerate this section"
 *     shortcut (deep-links to `/proposals/{id}/edit?step=9`)
 *   - Recommended improvements list (top 3-5, ordered by section
 *     score ascending — weakest first)
 *   - LLM kill-switch / low-confidence state: alignment + evidence
 *     dims render with a neutral gray dial + explainer chip; other
 *     three dims render as normal
 *
 * Response-code discrimination on the fetch:
 *   - 402 `proposals_premium_only`  → inline Premium upgrade CTA
 *   - 404 `feature_disabled`        → inline FeatureDisabled shell
 *   - 422 `quality_score_not_ready` → inline empty-state (points to step 9)
 *   - 422 `invalid_section_shape`   → inline retryable error banner
 *   - Everything else               → inline "Retry" banner (no toast)
 *
 * Kill-switch detection: `body.llm_disabled === true` OR
 * `body.llm_failure_reason` in the LLM-fallback set. The frontend
 * does NOT block export or the rest of the UI in these states —
 * it just tints alignment + evidence dims neutral.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  Gauge,
  Sparkles,
  ChevronRight,
  Info,
} from "lucide-react";

import { api, formatApiError } from "@/api/client";
import ScoreDial from "./ScoreDial";
import CoachPanel from "./CoachPanel";
import { SECTION_KEYS } from "./utils";
import {
  QUALITY_DISCLAIMER,
  QUALITY_STEP_TITLE,
  QUALITY_STEP_SUBTITLE,
  OVERALL_SCORE_LABEL,
  DIMENSION_LABELS,
  DIMENSION_EXPLAINERS,
  KILL_SWITCH_BADGE_LABEL,
  KILL_SWITCH_EXPLAINER,
  LOW_CONFIDENCE_EXPLAINER,
  WEAK_SECTION_CHIP_LABEL,
  WEAK_SECTION_TARGET_LABEL_PREFIX,
  WEAK_SECTION_TARGET_TITLE_PREFIX,
  RECOMMENDED_IMPROVEMENTS_TITLE,
  RECOMMENDED_IMPROVEMENTS_EMPTY,
  COMPUTE_SCORE_BUTTON,
  RECOMPUTE_SCORE_BUTTON,
  EMPTY_STATE_TITLE,
  EMPTY_STATE_BODY,
  EMPTY_STATE_CTA,
  RETRY_BUTTON,
  RETRY_BANNER_TITLE,
  FREE_TIER_QUALITY_TITLE,
  FREE_TIER_QUALITY_BODY,
  UPGRADE_CTA_LABEL,
  FEATURE_DISABLED_TITLE,
  FEATURE_DISABLED_BODY,
} from "./quality_copy";
import {
  sectionDisplayTitle,
  deriveWeakSections,
  isLlmDimNeutral,
} from "./quality_helpers";


// Recommended-improvements cap. Backend suggestions are already
// capped at 3 per section; here we show up to 5 across the whole
// proposal (weakest sections first).
const RECOMMENDED_IMPROVEMENTS_CAP = 5;


function QualityStateBanner({ title, body, tone = "warning", testId, action }) {
  const toneColour = tone === "error"
    ? { bg: "var(--v2-danger-soft)", border: "var(--v2-danger)", fg: "var(--v2-danger)" }
    : { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.45)", fg: "#f59e0b" };
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border p-6 flex items-start gap-3"
      style={{
        background: toneColour.bg,
        borderColor: toneColour.border,
        color: toneColour.fg,
      }}
    >
      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 space-y-2">
        <h4 className="text-v2-body font-semibold">{title}</h4>
        {body && <p className="text-v2-body-sm">{body}</p>}
        {action}
      </div>
    </div>
  );
}


function DimensionRow({ dimKey, value, neutral, testId }) {
  const label = DIMENSION_LABELS[dimKey] || dimKey;
  const explainer = DIMENSION_EXPLAINERS[dimKey] || "";
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-v2-border bg-v2-surfaceSubtle/60 p-4 flex items-center gap-4"
    >
      <ScoreDial
        score={value}
        neutral={neutral}
        size={72}
        testId={testId ? `${testId}-dial` : undefined}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-v2-body-sm font-semibold text-v2-text">
            {label}
          </span>
          {neutral && (
            <span
              data-testid={testId ? `${testId}-neutral-chip` : undefined}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-v2-caption uppercase tracking-[0.14em]"
              style={{
                background: "rgba(148,163,184,0.14)",
                border: "1px solid rgba(148,163,184,0.4)",
                color: "var(--v2-textMuted, #9ca3af)",
              }}
            >
              <Info className="w-3 h-3" /> {KILL_SWITCH_BADGE_LABEL}
            </span>
          )}
        </div>
        <p className="mt-1 text-v2-caption text-v2-textMuted leading-relaxed">
          {neutral ? KILL_SWITCH_EXPLAINER : explainer}
        </p>
      </div>
    </div>
  );
}


function SkeletonState() {
  return (
    <div data-testid="quality-score-skeleton" className="space-y-6">
      <div className="rounded-3xl border border-v2-border bg-v2-surface p-8 animate-pulse">
        <div className="h-3 w-40 rounded bg-v2-border/70 mb-4" />
        <div className="h-6 w-64 rounded bg-v2-border/70 mb-3" />
        <div className="h-3 w-full rounded bg-v2-border/50" />
        <div className="h-24 w-24 rounded-full bg-v2-border/50 mt-6" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 p-4 h-24 animate-pulse"
          />
        ))}
      </div>
    </div>
  );
}


function FreeTierPanel() {
  return (
    <div
      data-testid="quality-score-free-tier"
      className="rounded-3xl border border-v2-border bg-v2-surface p-8 flex flex-col gap-4"
    >
      <span className="text-v2-caption uppercase tracking-[0.28em] text-v2-accent">
        PREMIUM FEATURE
      </span>
      <h3 className="text-v2-heading-sm font-semibold text-v2-text">
        {FREE_TIER_QUALITY_TITLE}
      </h3>
      <p className="text-v2-body-sm text-v2-textMuted max-w-lg">
        {FREE_TIER_QUALITY_BODY}
      </p>
      <Link
        to="/billing"
        data-testid="quality-score-upgrade-cta"
        className="inline-flex w-fit items-center gap-2 rounded-full bg-v2-accent px-4 py-2 text-v2-body-sm font-semibold text-v2-accentInk hover:bg-v2-accentHover transition-colors"
      >
        {UPGRADE_CTA_LABEL}
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
}


function FeatureDisabledPanel() {
  return (
    <div
      data-testid="quality-score-feature-disabled"
      className="rounded-3xl border border-v2-border bg-v2-surface p-8 flex flex-col items-center text-center gap-4"
    >
      <div
        className="rounded-full p-3"
        style={{
          background: "rgba(148,163,184,0.14)",
          border: "1px solid rgba(148,163,184,0.4)",
        }}
      >
        <Info className="w-5 h-5 text-v2-textMuted" />
      </div>
      <h3 className="text-v2-heading-sm font-semibold text-v2-text">
        {FEATURE_DISABLED_TITLE}
      </h3>
      <p className="text-v2-body-sm text-v2-textMuted max-w-lg">
        {FEATURE_DISABLED_BODY}
      </p>
    </div>
  );
}


function EmptyStatePanel({ proposalId }) {
  return (
    <div
      data-testid="quality-score-empty"
      className="rounded-3xl border border-dashed border-v2-border bg-v2-surface/40 p-10 flex flex-col items-center text-center gap-4"
    >
      <Sparkles className="w-6 h-6 text-v2-accent" />
      <h3 className="text-v2-heading-sm font-semibold text-v2-text">
        {EMPTY_STATE_TITLE}
      </h3>
      <p className="max-w-lg text-v2-body-sm text-v2-textMuted">
        {EMPTY_STATE_BODY}
      </p>
      {proposalId && (
        <Link
          to={`/proposals/${proposalId}/edit?step=9`}
          data-testid="quality-score-empty-cta"
          className="inline-flex items-center gap-2 rounded-full bg-v2-accent px-4 py-2 text-v2-body-sm font-semibold text-v2-accentInk hover:bg-v2-accentHover transition-colors"
        >
          {EMPTY_STATE_CTA}
        </Link>
      )}
    </div>
  );
}


export default function QualityScoreStep({ proposalId }) {
  const [state, setState] = useState({
    loading: false,
    computing: false,
    body: null,
    error: null,
    isPremiumUpsell: false,
    isFeatureDisabled: false,
    isEmpty: false,
    hasComputed: false,
  });

  const compute = useCallback(async () => {
    if (!proposalId) return;
    setState((s) => ({ ...s, computing: true, error: null }));
    try {
      const res = await api.post(`/proposals/${proposalId}/quality-score`);
      setState({
        loading: false,
        computing: false,
        body: res.data,
        error: null,
        isPremiumUpsell: false,
        isFeatureDisabled: false,
        isEmpty: false,
        hasComputed: true,
      });
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "proposals_premium_only") {
        setState({
          loading: false, computing: false, body: null, error: null,
          isPremiumUpsell: true, isFeatureDisabled: false, isEmpty: false,
          hasComputed: true,
        });
        return;
      }
      if (code === "feature_disabled") {
        setState({
          loading: false, computing: false, body: null, error: null,
          isPremiumUpsell: false, isFeatureDisabled: true, isEmpty: false,
          hasComputed: true,
        });
        return;
      }
      if (code === "quality_score_not_ready") {
        setState({
          loading: false, computing: false, body: null, error: null,
          isPremiumUpsell: false, isFeatureDisabled: false, isEmpty: true,
          hasComputed: true,
        });
        return;
      }
      setState({
        loading: false,
        computing: false,
        body: null,
        error: formatApiError(err),
        isPremiumUpsell: false,
        isFeatureDisabled: false,
        isEmpty: false,
        hasComputed: true,
      });
    }
  }, [proposalId]);

  // Auto-compute on first mount. Users can click "Refresh score" to
  // recompute (charges 1 slot). Consuming the slot on mount is
  // intentional per user brief §wizard-step-10 activation set.
  useEffect(() => {
    compute();
  }, [compute]);

  const weakSections = useMemo(() => {
    if (!state.body) return [];
    return deriveWeakSections(state.body);
  }, [state.body]);

  const recommendedImprovements = useMemo(() => {
    if (!state.body) return [];
    // G4C CP2 polish rerun (2026-07-20) — prefer the server-derived
    // `weak_sections[*].recommended_improvement` copy over the
    // per-section `sections[*].suggestions` free-form array. When
    // the server-derived list is non-empty, we render its
    // dedupe'd copy (top RECOMMENDED_IMPROVEMENTS_CAP, ordered by
    // ascending section score — which the server already guarantees).
    // Only fall back to the legacy `sections[*].suggestions` path
    // when the server list is empty AND (legacy) suggestions exist.
    const server = weakSections
      .filter((s) => typeof s.recommendedImprovement === "string" && s.recommendedImprovement.length > 0);
    if (server.length > 0) {
      const seen = new Set();
      const items = [];
      for (const entry of server) {
        const text = entry.recommendedImprovement;
        if (seen.has(text)) continue;
        seen.add(text);
        items.push({ sectionKey: entry.sectionKey, text });
        if (items.length >= RECOMMENDED_IMPROVEMENTS_CAP) break;
      }
      return items;
    }
    // ---- Legacy fallback: `sections[*].suggestions` (pre-CP2 polish). ----
    if (!state.body?.sections) return [];
    const items = [];
    const entries = Object.entries(state.body.sections)
      .filter(([, entry]) => entry?.status === "passed")
      .sort((a, b) => (a[1].section_score ?? 0) - (b[1].section_score ?? 0));
    for (const [key, entry] of entries) {
      for (const suggestion of entry.suggestions || []) {
        items.push({
          sectionKey: key,
          text: suggestion,
        });
        if (items.length >= RECOMMENDED_IMPROVEMENTS_CAP) return items;
      }
    }
    return items;
  }, [state.body, weakSections]);

  // ---- Non-happy paths first ----

  if (state.isPremiumUpsell) {
    return (
      <div data-testid="wizard-step-quality_score" className="space-y-6">
        <VoiceDisclaimer />
        <FreeTierPanel />
      </div>
    );
  }

  if (state.isFeatureDisabled) {
    return (
      <div data-testid="wizard-step-quality_score" className="space-y-6">
        <FeatureDisabledPanel />
      </div>
    );
  }

  if (state.isEmpty) {
    return (
      <div data-testid="wizard-step-quality_score" className="space-y-6">
        <VoiceDisclaimer />
        <EmptyStatePanel proposalId={proposalId} />
        {/* G4D CP2 — Coach panel is orthogonal to the quality-score
            state. Coach's own locked-state handles the "no generated
            sections" case, so mounting here is safe. */}
        <CoachPanel proposalId={proposalId} />
      </div>
    );
  }

  if (state.computing && !state.body) {
    return (
      <div data-testid="wizard-step-quality_score" className="space-y-6">
        <VoiceDisclaimer />
        <SkeletonState />
        {/* G4D CP2 — Coach available while the quality-score
            computation is in flight. */}
        <CoachPanel proposalId={proposalId} />
      </div>
    );
  }

  if (state.error) {
    return (
      <div data-testid="wizard-step-quality_score" className="space-y-6">
        <VoiceDisclaimer />
        <QualityStateBanner
          testId="quality-score-error"
          title={RETRY_BANNER_TITLE}
          body={state.error}
          tone="error"
          action={
            <button
              type="button"
              onClick={compute}
              disabled={state.computing}
              data-testid="quality-score-retry"
              className="inline-flex items-center gap-2 rounded-full border border-v2-danger px-3 py-1.5 text-v2-caption font-semibold text-v2-danger hover:bg-v2-danger/10 transition-colors disabled:opacity-50"
            >
              {state.computing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              {RETRY_BUTTON}
            </button>
          }
        />
        {/* G4D CP2 — Coach panel is orthogonal to the quality-score
            state. When quality-score computation errors, Coach still
            works as long as at least one section is generated —
            surface it here so the user isn't blocked from coaching
            by a transient score-computation failure. */}
        <CoachPanel proposalId={proposalId} />
      </div>
    );
  }

  if (!state.body) {
    // Shouldn't happen (auto-compute runs on mount), but a safe fallback.
    return (
      <div data-testid="wizard-step-quality_score" className="space-y-6">
        <VoiceDisclaimer />
        <SkeletonState />
      </div>
    );
  }

  // ---- Happy path — full render ----

  const body = state.body;
  const isKillSwitch = body.llm_disabled === true;
  const failureReason = body.llm_failure_reason;
  const llmNeutral = isLlmDimNeutral(body);
  const llmExplainer = isKillSwitch
    ? KILL_SWITCH_EXPLAINER
    : (failureReason && failureReason !== null ? LOW_CONFIDENCE_EXPLAINER : null);

  return (
    <div data-testid="wizard-step-quality_score" className="space-y-6">
      <VoiceDisclaimer />

      <section className="rounded-3xl border border-v2-border bg-v2-surface p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex items-center gap-6">
          <ScoreDial
            score={body.overall_score}
            label={OVERALL_SCORE_LABEL}
            size={128}
            testId="quality-score-overall"
          />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Gauge className="w-4 h-4 text-v2-accent" />
            <h3 className="text-v2-heading-sm font-semibold text-v2-text">
              {QUALITY_STEP_TITLE}
            </h3>
          </div>
          <p className="text-v2-body-sm text-v2-textMuted max-w-xl leading-relaxed">
            {QUALITY_STEP_SUBTITLE}
          </p>
          {llmExplainer && (
            <div
              data-testid="quality-score-llm-state-explainer"
              className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-v2-caption"
              style={{
                background: "rgba(148,163,184,0.12)",
                border: "1px solid rgba(148,163,184,0.4)",
                color: "var(--v2-textMuted, #9ca3af)",
              }}
            >
              <Info className="w-3.5 h-3.5" />
              {llmExplainer}
            </div>
          )}
          <div className="pt-3">
            <button
              type="button"
              onClick={compute}
              disabled={state.computing}
              data-testid="quality-score-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-v2-accent px-3 py-1.5 text-v2-caption font-semibold text-v2-accent hover:bg-v2-accent/10 transition-colors disabled:opacity-50"
            >
              {state.computing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              {state.hasComputed ? RECOMPUTE_SCORE_BUTTON : COMPUTE_SCORE_BUTTON}
            </button>
          </div>
        </div>
      </section>

      {/* 5-dimension breakdown */}
      <section
        data-testid="quality-score-dimensions"
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        {["completeness", "length", "readability"].map((dim) => {
          // Section-average value for the dim (mean across all sections).
          const value = _dimAverage(body.sections, dim);
          return (
            <DimensionRow
              key={dim}
              dimKey={dim}
              value={value}
              neutral={false}
              testId={`quality-score-dim-${dim}`}
            />
          );
        })}
        {["alignment", "evidence"].map((dim) => {
          const value = _dimAverage(body.sections, dim);
          return (
            <DimensionRow
              key={dim}
              dimKey={dim}
              value={value}
              neutral={llmNeutral}
              testId={`quality-score-dim-${dim}`}
            />
          );
        })}
      </section>

      {/* Weak sections */}
      {weakSections.length > 0 && (
        <section
          data-testid="quality-score-weak-sections"
          className="rounded-3xl border border-v2-border bg-v2-surface p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-4 h-4 text-v2-warning" />
            <h4 className="text-v2-body font-semibold text-v2-text">
              Sections that may benefit from a refresh
            </h4>
          </div>
          <ul className="space-y-3">
            {weakSections.map((s) => (
              <li
                key={s.sectionKey}
                data-testid={`weak-section-${s.sectionKey}`}
                className="flex items-start justify-between gap-4 flex-wrap rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-v2-body-sm font-semibold text-v2-text">
                      {sectionDisplayTitle(s.sectionKey)}
                    </span>
                    <span
                      data-testid={`weak-section-chip-${s.sectionKey}`}
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-v2-caption uppercase tracking-[0.12em]"
                      style={{
                        background: "rgba(245,158,11,0.14)",
                        border: "1px solid rgba(245,158,11,0.45)",
                        color: "var(--v2-warning, #f59e0b)",
                      }}
                    >
                      {WEAK_SECTION_CHIP_LABEL}
                    </span>
                    {typeof s.threshold === "number" && (
                      <span
                        data-testid={`weak-section-target-${s.sectionKey}`}
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-v2-caption tracking-[0.08em] text-v2-textMuted"
                        style={{
                          background: "rgba(148,163,184,0.10)",
                          border: "1px solid rgba(148,163,184,0.35)",
                        }}
                        title={`${WEAK_SECTION_TARGET_TITLE_PREFIX}${s.threshold} / 100`}
                      >
                        {WEAK_SECTION_TARGET_LABEL_PREFIX}
                        {s.threshold}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-v2-caption text-v2-textMuted">
                    Section score: {s.sectionScore} / 100
                    {s.weakestDim ? ` · Weakest dimension: ${DIMENSION_LABELS[s.weakestDim] || s.weakestDim}` : ""}
                  </p>
                  {s.recommendedImprovement && (
                    <p
                      data-testid={`weak-section-improvement-${s.sectionKey}`}
                      className="mt-2 text-v2-body-sm leading-relaxed text-v2-text/80"
                    >
                      {s.recommendedImprovement}
                    </p>
                  )}
                </div>
                <Link
                  to={`/proposals/${proposalId}/edit?step=9`}
                  data-testid={`weak-section-regen-${s.sectionKey}`}
                  className="inline-flex items-center gap-1 text-v2-caption font-semibold text-v2-accent hover:underline"
                >
                  Regenerate section
                  <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Recommended improvements */}
      <section
        data-testid="quality-score-recommendations"
        className="rounded-3xl border border-v2-border bg-v2-surface p-6"
      >
        <h4 className="text-v2-body font-semibold text-v2-text mb-3">
          {RECOMMENDED_IMPROVEMENTS_TITLE}
        </h4>
        {recommendedImprovements.length === 0 ? (
          <p
            data-testid="quality-score-recommendations-empty"
            className="text-v2-body-sm text-v2-textMuted"
          >
            {RECOMMENDED_IMPROVEMENTS_EMPTY}
          </p>
        ) : (
          <ul className="space-y-2">
            {recommendedImprovements.map((it, i) => (
              <li
                key={`${it.sectionKey}-${i}`}
                data-testid={`recommendation-${i}`}
                className="flex items-start gap-3 text-v2-body-sm text-v2-textMuted"
              >
                <span className="text-v2-accent">•</span>
                <span>
                  <span className="text-v2-text font-medium">
                    {sectionDisplayTitle(it.sectionKey)}:
                  </span>{" "}
                  {it.text}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* G4D CP2 — AI Grant Coach inline panel. Renders below the
          quality-score actionability bridge. Locked when the proposal
          has zero passed sections. */}
      <CoachPanel proposalId={proposalId} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Local helpers — mean of a dimension across passed sections.
// ---------------------------------------------------------------------------

function _dimAverage(sections, dimKey) {
  if (!sections) return 0;
  const passed = Object.values(sections).filter((s) => s?.status === "passed");
  if (passed.length === 0) return 0;
  const total = passed.reduce((acc, s) => acc + (s?.dimensions?.[dimKey] ?? 0), 0);
  return Math.round(total / passed.length);
}


// ---------------------------------------------------------------------------
// Voice disclaimer — locked copy, always rendered above the score.
// ---------------------------------------------------------------------------

function VoiceDisclaimer() {
  return (
    <div
      data-testid="quality-score-disclaimer"
      className="rounded-2xl border border-v2-border/60 bg-v2-surface/30 px-4 py-3 flex items-start gap-2"
    >
      <Info className="w-4 h-4 flex-shrink-0 text-v2-accent mt-0.5" />
      <p className="text-v2-caption text-v2-textMuted leading-relaxed">
        {QUALITY_DISCLAIMER}
      </p>
    </div>
  );
}
